/**
 * Aggregator Operations — Reusable Utilities
 *
 * - BrandReadiness: Cross-checks campaign targets vs actual submissions.
 * - ExcelExportService: Generates professional aggregator-ready Excel files.
 */

import * as XLSX from 'xlsx-js-style';
import type { Campaign, CampaignSubmission, SubmissionItem } from './types';
import { DiscountType, FundingType, SubmissionStatus } from './types';
import type { Brand } from '../../domain/entities';

/** Truncate text to fit within Excel's 32 767-character cell limit. */
const xlTrunc = (v: string, max = 32767) => (v.length > max ? v.slice(0, max - 3) + '...' : v);

// ─── Target Comparison Utility (Shared) ──────────────────────────────────────

export type TargetStatus = 'PARTIAL' | 'COMPLETE' | 'OVER_SUBMITTED';

export interface TargetComparison {
  actual: number;
  target: number;
  status: TargetStatus;
  label: string;            // e.g. "2/1 (Extra Data)"
  shortLabel: string;       // e.g. "2/1"
}

/**
 * Compare the actual item count vs. the campaign target.
 * Returns a typed result with status, ratio label, and display helpers.
 */
export function compareTarget(actual: number, target: number): TargetComparison {
  const status: TargetStatus =
    actual < target ? 'PARTIAL'
    : actual === target ? 'COMPLETE'
    : 'OVER_SUBMITTED';

  const suffix =
    status === 'PARTIAL' ? ' (Incomplete)'
    : status === 'OVER_SUBMITTED' ? ' (Extra Data)'
    : '';

  return {
    actual,
    target,
    status,
    label: `${actual}/${target}${suffix}`,
    shortLabel: `${actual}/${target}`,
  };
}

/** CSS color tokens per TargetStatus for consistent theming. */
export const TARGET_STATUS_COLORS: Record<TargetStatus, { bg: string; text: string; border: string; dot: string }> = {
  PARTIAL:        { bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500' },
  COMPLETE:       { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  OVER_SUBMITTED: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
};

// ─── Item Change Detection ───────────────────────────────────────────────────

/** Describes a single field-level change between old and new item data. */
export interface ItemChange {
  field: 'name' | 'price' | 'description' | 'imageUrl';
  label: string;
}

/**
 * Compare old item data with new item data and return human-readable change descriptions.
 * Returns an empty array if nothing changed.
 */
export function detectItemChanges(
  oldItem: { name: string; price: number; description: string; imageUrl: string },
  newItem: { name: string; price: number; description: string; imageUrl: string },
): string[] {
  const changes: string[] = [];

  if (oldItem.name.trim() !== newItem.name.trim()) {
    changes.push(`Renamed from "${oldItem.name}" to "${newItem.name}"`);
  }

  if (oldItem.price !== newItem.price) {
    changes.push(`Updated Price from ${oldItem.price.toFixed(2)} to ${newItem.price.toFixed(2)}`);
  }

  const oldDesc = oldItem.description.trim();
  const newDesc = newItem.description.trim();
  if (oldDesc !== newDesc) {
    if (!oldDesc && newDesc) {
      changes.push('Added missing description');
    } else if (oldDesc && !newDesc) {
      changes.push('Removed description');
    } else {
      changes.push('Updated description');
    }
  }

  const oldImg = oldItem.imageUrl.trim();
  const newImg = newItem.imageUrl.trim();
  if (oldImg !== newImg) {
    if (!oldImg && newImg) {
      changes.push('Uploaded a new photo');
    } else if (oldImg && !newImg) {
      changes.push('Removed photo');
    } else {
      changes.push('Changed photo');
    }
  }

  return changes;
}

// ─── Brand Readiness Types ───────────────────────────────────────────────────

export type BrandReadinessStatus = 'READY' | 'IN_REVIEW' | 'MISSING';

export interface BrandReadinessEntry {
  brandId: string;
  brandName: string;
  status: BrandReadinessStatus;
  /** Submission (if any) for this brand/campaign combo */
  submission?: CampaignSubmission;
  /** Item count for READY brands */
  itemCount: number;
  /** Current review stage (for IN_REVIEW) */
  currentStage?: string;
  /** Branch-level breakdown for this brand (branch name + menu profile status) */
  branches?: { branchId: string; branchName: string; menuProfileId?: string; menuProfileName?: string; hasMenu: boolean }[];
}

export interface CampaignReadiness {
  campaign: Campaign;
  brands: BrandReadinessEntry[];
  readyCount: number;
  inReviewCount: number;
  missingCount: number;
  totalItems: number;
  /** All DEPLOYMENT_READY submissions for this campaign */
  readySubmissions: CampaignSubmission[];
}

// ─── Readiness Check Logic ───────────────────────────────────────────────────

/**
 * Cross-check campaign.brandIds against actual submissions to determine
 * per-brand readiness status.
 */
export function computeCampaignReadiness(
  campaign: Campaign,
  allSubmissions: CampaignSubmission[],
  allBrands: Brand[],
): CampaignReadiness {
  // Resolve which brands are targeted
  const targetBrandIds = campaign.targetAllBrands
    ? allBrands.map(b => b.id)
    : campaign.brandIds;

  // Get ALL submissions for this campaign (any status)
  const campSubs = allSubmissions.filter(s => s.campaignId === campaign.id);

  const brands: BrandReadinessEntry[] = targetBrandIds.map(brandId => {
    const brand = allBrands.find(b => b.id === brandId);
    const brandName = brand?.name ?? brandId;
    // Find the most relevant submission for this brand+campaign
    const sub = campSubs.find(s => s.brandId === brandId);

    if (!sub) {
      return { brandId, brandName, status: 'MISSING' as const, itemCount: 0 };
    }

    const activeItemCount = sub.items.filter(it => it.itemStatus !== 'DISCARDED').length;

    if (sub.status === SubmissionStatus.DEPLOYMENT_READY) {
      return { brandId, brandName, status: 'READY' as const, submission: sub, itemCount: activeItemCount };
    }

    // PENDING, IN_REVIEW, APPROVED — all count as "in review" (pipeline not yet complete)
    if ([SubmissionStatus.PENDING, SubmissionStatus.IN_REVIEW, SubmissionStatus.APPROVED].includes(sub.status)) {
      return { brandId, brandName, status: 'IN_REVIEW' as const, submission: sub, itemCount: activeItemCount, currentStage: sub.currentStage };
    }

    // REJECTED — treat as missing (needs resubmission)
    if (sub.status === SubmissionStatus.REJECTED) {
      return { brandId, brandName, status: 'MISSING' as const, submission: sub, itemCount: 0 };
    }

    // COMPLETED — already launched, exclude from readiness (show in Live tab only)
    if (sub.status === SubmissionStatus.COMPLETED) {
      return { brandId, brandName, status: 'MISSING' as const, submission: sub, itemCount: 0 };
    }

    return { brandId, brandName, status: 'READY' as const, submission: sub, itemCount: activeItemCount };
  });

  const readyCount = brands.filter(b => b.status === 'READY').length;
  const inReviewCount = brands.filter(b => b.status === 'IN_REVIEW').length;
  const missingCount = brands.filter(b => b.status === 'MISSING').length;
  const totalItems = brands.filter(b => b.status === 'READY').reduce((acc, b) => acc + b.itemCount, 0);
  const readySubmissions = brands.filter(b => b.status === 'READY' && b.submission).map(b => b.submission!);

  return { campaign, brands, readyCount, inReviewCount, missingCount, totalItems, readySubmissions };
}

// ─── Approved-Items Helpers (Single Source of Truth) ─────────────────────────

/** Return only non-DISCARDED items from a single submission. */
export function getApprovedItems(sub: CampaignSubmission): SubmissionItem[] {
  return sub.items.filter(it => it.itemStatus !== 'DISCARDED');
}

/**
 * Return the final approved menu across all READY submissions in a campaign.
 * This is the authoritative list that goes to aggregators — zero discarded items.
 */
export function getFinalMenuForCampaign(readiness: CampaignReadiness): SubmissionItem[] {
  return readiness.readySubmissions.flatMap(getApprovedItems);
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ExportPayload {
  campaign: Campaign;
  submission: CampaignSubmission;
  aggregatorName: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDateExcel(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9_\-. ]/g, '').replace(/\s+/g, '_');
}

function calcDiscountedPrice(originalPrice: number, campaign: Campaign): number {
  if (campaign.discountType === DiscountType.PERCENTAGE) {
    return Math.round(originalPrice * (1 - campaign.discountValue / 100) * 100) / 100;
  }
  // FLAT discount
  return Math.max(0, Math.round((originalPrice - campaign.discountValue) * 100) / 100);
}

// ─── Column Width Auto-Fit ───────────────────────────────────────────────────

function autoFitColumns(data: Record<string, unknown>[]): XLSX.ColInfo[] {
  if (data.length === 0) return [];
  const keys = Object.keys(data[0]);
  return keys.map(key => {
    const maxLen = Math.max(
      key.length,
      ...data.map(row => String(row[key] ?? '').length),
    );
    return { wch: Math.min(maxLen + 4, 50) };
  });
}

// ─── Professional Header Styling ─────────────────────────────────────────────
// Note: xlsx community edition supports basic cell styling through s property.
// For full styling we use header formatting compatible with SheetJS open-source.

function applyHeaderStyle(ws: XLSX.WorkSheet, colCount: number): void {
  const headerRange = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let c = headerRange.s.c; c <= Math.min(headerRange.e.c, colCount - 1); c++) {
    const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[cellAddr]) {
      // Bold + grey background for professional look
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: '1F2937' } },
        fill: { fgColor: { rgb: 'E5E7EB' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          bottom: { style: 'thin', color: { rgb: '9CA3AF' } },
        },
      };
    }
  }
}

// ─── ExcelExportService ──────────────────────────────────────────────────────

export class ExcelExportService {
  /**
   * Generate and download an aggregator-ready Excel file.
   */
  static exportForAggregator({ campaign, submission, aggregatorName }: ExportPayload): string {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Campaign Overview ────────────────────────────────────────

    const fundingLabel =
      campaign.fundingType === FundingType.CO_FUNDED ? 'Co-Funded'
      : campaign.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded'
      : 'No Funding';

    const overviewData = [
      { Field: 'Campaign Name', Value: campaign.name },
      { Field: 'Campaign Type', Value: campaign.discountType === DiscountType.PERCENTAGE ? 'Percentage Discount' : 'Flat Discount' },
      { Field: 'Discount', Value: campaign.discountType === DiscountType.PERCENTAGE ? `${campaign.discountValue}%` : `QAR ${campaign.discountValue}` },
      { Field: 'Start Date', Value: fmtDateExcel(campaign.validFrom) },
      { Field: 'End Date', Value: fmtDateExcel(campaign.validTo) },
      { Field: 'Target Aggregator', Value: aggregatorName },
      { Field: 'Funding Type', Value: fundingLabel },
      ...(campaign.fundingType === FundingType.CO_FUNDED ? [
        { Field: 'Sterling Share', Value: `${campaign.sterlingSharePct ?? 0}%` },
        { Field: 'Partner Share', Value: `${campaign.partnerSharePct ?? 0}%` },
      ] : []),
      { Field: 'Aggregators', Value: xlTrunc(campaign.aggregators.join(', ')) },
      { Field: 'Brand', Value: submission.brandName },
      { Field: 'Total Approved Items', Value: String(submission.items.length) },
      { Field: 'Submitted By', Value: submission.submitterName },
      ...(campaign.importantRequirements ? [{ Field: 'Requirements', Value: xlTrunc(campaign.importantRequirements) }] : []),
      { Field: 'Export Date', Value: fmtDateExcel(Date.now()) },
    ];

    const ws1 = XLSX.utils.json_to_sheet(overviewData);
    ws1['!cols'] = autoFitColumns(overviewData);
    applyHeaderStyle(ws1, 2);
    XLSX.utils.book_append_sheet(wb, ws1, 'Campaign Overview');

    // ── Sheet 2: Approved Menu Items ──────────────────────────────────────

    const itemsData = submission.items.map((item: SubmissionItem, idx: number) => ({
      '#': idx + 1,
      'Brand Name': submission.brandName,
      'Item Name': xlTrunc(item.name),
      'Description': xlTrunc(item.description ?? ''),
      'Original Price (QAR)': item.price,
      'Discounted Price (QAR)': calcDiscountedPrice(item.price, campaign),
      'Discount Applied': campaign.discountType === DiscountType.PERCENTAGE
        ? `${campaign.discountValue}%`
        : `QAR ${campaign.discountValue}`,
      'Image URL': xlTrunc(item.imageUrl ?? ''),
    }));

    const ws2 = XLSX.utils.json_to_sheet(itemsData);
    ws2['!cols'] = autoFitColumns(itemsData as Record<string, unknown>[]);
    applyHeaderStyle(ws2, 8);

    // Auto-filter on the Menu sheet for easy sorting/filtering by aggregators
    ws2['!autofilter'] = { ref: `A1:H${itemsData.length + 1}` };

    XLSX.utils.book_append_sheet(wb, ws2, 'Approved Menu Items');

    // ── Generate & Download ───────────────────────────────────────────────

    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `${sanitizeFilename(submission.brandName)}_${sanitizeFilename(campaign.name)}_${sanitizeFilename(aggregatorName)}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);

    return fileName;
  }

  /**
   * Generate the filename without downloading — for preview/status tracking.
   */
  static getExportFileName(campaign: Campaign, submission: CampaignSubmission, aggregatorName: string): string {
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${sanitizeFilename(submission.brandName)}_${sanitizeFilename(campaign.name)}_${sanitizeFilename(aggregatorName)}_${dateStr}.xlsx`;
  }

  /**
   * Bulk export for all aggregators of a campaign in one go.
   */
  static exportAllAggregators(campaign: Campaign, submission: CampaignSubmission): string[] {
    const fileNames: string[] = [];
    for (const agg of campaign.aggregators) {
      const fn = ExcelExportService.exportForAggregator({ campaign, submission, aggregatorName: agg });
      fileNames.push(fn);
    }
    return fileNames;
  }

  /**
   * Export a file for a specific brand's submission.
   * Used by the Deployment Center "Export per Brand" engine.
   */
  static exportMasterForBrand(
    campaign: Campaign,
    submission: CampaignSubmission,
  ): string {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Campaign Overview ────────────────────────────────────────
    const fundingLabel =
      campaign.fundingType === FundingType.CO_FUNDED ? 'Co-Funded'
      : campaign.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded'
      : 'No Funding';

    const overviewData = [
      { Field: 'Campaign Name', Value: campaign.name },
      { Field: 'Campaign Type', Value: campaign.discountType === DiscountType.PERCENTAGE ? 'Percentage Discount' : 'Flat Discount' },
      { Field: 'Discount', Value: campaign.discountType === DiscountType.PERCENTAGE ? `${campaign.discountValue}%` : `QAR ${campaign.discountValue}` },
      { Field: 'Start Date', Value: fmtDateExcel(campaign.validFrom) },
      { Field: 'End Date', Value: fmtDateExcel(campaign.validTo) },
      { Field: 'Brand', Value: submission.brandName },
      { Field: 'Funding Type', Value: fundingLabel },
      ...(campaign.fundingType === FundingType.CO_FUNDED ? [
        { Field: 'Sterling Share', Value: `${campaign.sterlingSharePct ?? 0}%` },
        { Field: 'Partner Share', Value: `${campaign.partnerSharePct ?? 0}%` },
      ] : []),
      { Field: 'Aggregators', Value: xlTrunc(campaign.aggregators.join(', ')) },
      { Field: 'Total Items', Value: String(submission.items.length) },
      { Field: 'Submitted By', Value: submission.submitterName },
      ...(campaign.requiredItemCount && campaign.requiredItemCount > 0 ? [
        { Field: 'Target Items per Brand', Value: String(campaign.requiredItemCount) },
      ] : []),
      ...(campaign.importantRequirements ? [{ Field: 'Requirements', Value: xlTrunc(campaign.importantRequirements) }] : []),
      { Field: 'Export Date', Value: fmtDateExcel(Date.now()) },
    ];

    const ws1 = XLSX.utils.json_to_sheet(overviewData);
    ws1['!cols'] = autoFitColumns(overviewData);
    applyHeaderStyle(ws1, 2);
    XLSX.utils.book_append_sheet(wb, ws1, 'Campaign Overview');

    // ── Sheet 2: Approved Menu Items for this brand ───────────────────────
    const itemsData = submission.items.map((item: SubmissionItem, idx: number) => ({
      '#': idx + 1,
      'Brand Name': submission.brandName,
      'Item Name': xlTrunc(item.name),
      'Description': xlTrunc(item.description ?? ''),
      'Original Price (QAR)': item.price,
      'Discounted Price (QAR)': calcDiscountedPrice(item.price, campaign),
      'Discount Applied': campaign.discountType === DiscountType.PERCENTAGE
        ? `${campaign.discountValue}%`
        : `QAR ${campaign.discountValue}`,
      'Image URL': xlTrunc(item.imageUrl ?? ''),
    }));

    const ws2 = XLSX.utils.json_to_sheet(itemsData);
    ws2['!cols'] = autoFitColumns(itemsData as Record<string, unknown>[]);
    applyHeaderStyle(ws2, 8);
    ws2['!autofilter'] = { ref: `A1:H${itemsData.length + 1}` };
    XLSX.utils.book_append_sheet(wb, ws2, 'Approved Menu Items');

    // ── Generate & Download ───────────────────────────────────────────────
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `${sanitizeFilename(submission.brandName)}_${sanitizeFilename(campaign.name)}_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
    return fileName;
  }

  /**
   * Export a single unified MASTER DISPATCH file for an entire campaign.
   * Sheet 1: Campaign Overview (metadata, co-fund split, all aggregators)
   * Sheet 2: Brand Readiness Summary (brand-by-brand status grid)
   * Sheet 3+: Per-brand menu sheets with approved items.
   */
  static exportMasterDispatch(
    campaign: Campaign,
    readySubmissions: CampaignSubmission[],
    brandEntries: BrandReadinessEntry[],
  ): string {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Campaign Overview ────────────────────────────────────────
    const fundingLabel =
      campaign.fundingType === FundingType.CO_FUNDED ? 'Co-Funded'
      : campaign.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded'
      : 'No Funding';

    const overviewData = [
      { Field: 'Campaign Name', Value: campaign.name },
      { Field: 'Campaign Type', Value: campaign.discountType === DiscountType.PERCENTAGE ? 'Percentage Discount' : 'Flat Discount' },
      { Field: 'Discount', Value: campaign.discountType === DiscountType.PERCENTAGE ? `${campaign.discountValue}%` : `QAR ${campaign.discountValue}` },
      { Field: 'Start Date', Value: fmtDateExcel(campaign.validFrom) },
      { Field: 'End Date', Value: fmtDateExcel(campaign.validTo) },
      { Field: 'Funding Type', Value: fundingLabel },
      ...(campaign.fundingType === FundingType.CO_FUNDED ? [
        { Field: 'Sterling Share', Value: `${campaign.sterlingSharePct ?? 0}%` },
        { Field: 'Partner Share', Value: `${campaign.partnerSharePct ?? 0}%` },
      ] : []),
      { Field: 'Aggregators', Value: xlTrunc(campaign.aggregators.join(', ')) },
      { Field: 'Brands Ready', Value: xlTrunc(readySubmissions.map(s => s.brandName).join(', ')) },
      { Field: 'Ready / Total Brands', Value: `${readySubmissions.length} / ${brandEntries.length}` },
      { Field: 'Total Items', Value: String(readySubmissions.reduce((acc, s) => acc + s.items.length, 0)) },
      ...(campaign.requiredItemCount && campaign.requiredItemCount > 0 ? [
        { Field: 'Target Items per Brand', Value: String(campaign.requiredItemCount) },
      ] : []),
      ...(campaign.importantRequirements ? [{ Field: 'Requirements', Value: xlTrunc(campaign.importantRequirements) }] : []),
      { Field: 'Export Date', Value: fmtDateExcel(Date.now()) },
    ];

    const ws1 = XLSX.utils.json_to_sheet(overviewData);
    ws1['!cols'] = autoFitColumns(overviewData);
    applyHeaderStyle(ws1, 2);
    XLSX.utils.book_append_sheet(wb, ws1, 'Campaign Overview');

    // ── Sheet 2: Brand Readiness Summary ──────────────────────────────────
    const targetCount = campaign.requiredItemCount ?? 0;
    const brandData = brandEntries.map((b, i) => ({
      '#': i + 1,
      'Brand': b.brandName,
      'Status': b.status,
      'Actual Items': b.itemCount,
      ...(targetCount > 0 ? { 'Target Items': targetCount, 'Balance': b.itemCount >= targetCount ? 'COMPLETE' : b.itemCount > 0 ? 'PARTIAL' : 'MISSING' } : {}),
      'Current Stage': b.currentStage ?? (b.status === 'READY' ? 'Complete' : b.status === 'MISSING' ? 'Not Submitted' : '—'),
      'Submitted By': b.submission?.submitterName ?? '—',
    }));

    const brandSheetData = brandData.length > 0
      ? brandData
      : [{ '#': 1, 'Brand': '—', 'Status': 'N/A', 'Items': 0, 'Current Stage': '—', 'Submitted By': '—' }];

    const ws2 = XLSX.utils.json_to_sheet(brandSheetData);
    ws2['!cols'] = autoFitColumns(brandSheetData as Record<string, unknown>[]);
    applyHeaderStyle(ws2, 6);
    XLSX.utils.book_append_sheet(wb, ws2, 'Brand Readiness');

    // ── Sheet 3+: One sheet per brand ──────────────────────────────────────
    const usedNames = new Set(['Campaign Overview', 'Brand Readiness']);
    for (const sub of readySubmissions) {
      const itemsData = sub.items.map((item: SubmissionItem, idx: number) => ({
        '#': idx + 1,
        'Item Name': xlTrunc(item.name),
        'Description': xlTrunc(item.description ?? ''),
        'Original Price (QAR)': item.price,
        'Discounted Price (QAR)': calcDiscountedPrice(item.price, campaign),
        'Discount Applied': campaign.discountType === DiscountType.PERCENTAGE
          ? `${campaign.discountValue}%`
          : `QAR ${campaign.discountValue}`,
        'Image URL': xlTrunc(item.imageUrl ?? ''),
      }));

      const sheetData = itemsData.length > 0
        ? itemsData
        : [{ '#': 1, 'Item Name': 'No items', 'Description': '', 'Original Price (QAR)': '', 'Discounted Price (QAR)': '', 'Discount Applied': '', 'Image URL': '' }];
      const wsBrand = XLSX.utils.json_to_sheet(sheetData);
      wsBrand['!cols'] = autoFitColumns(sheetData as Record<string, unknown>[]);
      applyHeaderStyle(wsBrand, 7);
      if (itemsData.length > 0) {
        wsBrand['!autofilter'] = { ref: `A1:G${itemsData.length + 1}` };
      }
      // Sanitize sheet name: remove invalid chars, max 31 chars, avoid duplicates
      let sheetName = sub.brandName.replace(/[\\/*?:\[\]]/g, '').trim().slice(0, 31) || 'Brand';
      while (usedNames.has(sheetName)) { sheetName = sheetName.slice(0, 28) + `_${usedNames.size}`; }
      usedNames.add(sheetName);
      XLSX.utils.book_append_sheet(wb, wsBrand, sheetName);
    }

    // ── Generate & Download ───────────────────────────────────────────────
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `${sanitizeFilename(campaign.name)}_MASTER_DISPATCH_${dateStr}.xlsx`;

    XLSX.writeFile(wb, fileName);
    return fileName;
  }
}
